import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { questionsAPI, nodesAPI } from '../lib/api';
import { MindmapViewer } from '../components/mindmap/MindmapViewer';
import { GitBranch, ExternalLink } from 'lucide-react';

export default function EmbedPage() {
  const { questionId } = useParams();
  const [searchParams] = useSearchParams();
  const highlightNodeId = searchParams.get('node');
  
  const [question, setQuestion] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [questionId]);

  const fetchData = async () => {
    try {
      const [questionRes, nodesRes] = await Promise.all([
        questionsAPI.getOne(questionId),
        nodesAPI.getByQuestion(questionId),
      ]);
      setQuestion(questionRes.data);
      setNodes(nodesRes.data);
    } catch (err) {
      console.error('Error loading embed:', err);
      setError('Failed to load mindmap');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-3"></div>
          <p className="text-sm text-slate-500">Loading mindmap...</p>
        </div>
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <GitBranch className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">{error || 'Mindmap not found'}</p>
        </div>
      </div>
    );
  }

  const fullUrl = `${window.location.origin}/q/${questionId}`;

  return (
    <div className="h-screen bg-white flex flex-col" data-testid="embed-page">
      {/* Mini Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 min-w-0">
          <GitBranch className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <span className="font-medium text-slate-900 truncate text-sm">
            {question.title}
          </span>
        </div>
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-orange-500 transition-colors flex-shrink-0"
          data-testid="open-in-clarities"
        >
          <span className="hidden sm:inline">Open in Clarities</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      
      {/* Mindmap Viewer */}
      <div className="flex-1 relative">
        <MindmapViewer
          nodes={nodes}
          rootNodeId={question.root_node_id}
          highlightNodeId={highlightNodeId}
          readOnly={true}
          onAddChild={() => window.open(fullUrl, '_blank')}
          onVote={() => window.open(fullUrl, '_blank')}
          onComment={() => window.open(fullUrl, '_blank')}
          onViewContent={() => window.open(fullUrl, '_blank')}
          userVotes={{}}
        />
      </div>
      
      {/* Footer Branding */}
      <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-center">
        <a
          href="https://clarities.app"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
        >
          <GitBranch className="w-3 h-3" />
          Powered by Clarities
        </a>
      </div>
    </div>
  );
}
